import React, { useState, useEffect } from 'react';

const FilterModal = ({ 
  isOpen, 
  onClose, 
  skills, 
  selectedTags, 
  onTagsChange, 
  searchQuery, 
  onSearchChange, 
  onApplyFilters 
}) => {
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery || '');
  const [localSelectedTags, setLocalSelectedTags] = useState(selectedTags || []);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLocalSearchQuery(searchQuery || '');
      setLocalSelectedTags(selectedTags || []);
      // Ensure the modal mounts hidden, then become visible on next frame so the CSS transition runs
      setVisible(false);
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    } else {
      setVisible(false);
    }
  }, [isOpen, searchQuery, selectedTags]);

  // Handle Enter key press and prevent page scrolling
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Enter' && isOpen) {
        handleApply();
      }
      if (e.key === 'Escape' && isOpen) {
        handleCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }
  }, [isOpen, localSearchQuery, localSelectedTags]);

  // Prevent page scrolling while modal is open (but allow modal internal scrolling)
  useEffect(() => {
    if (!isOpen) return;

    const preventBodyScroll = (e) => {
      // Only prevent scroll if the event target is not inside the modal
      const modalElement = e.target.closest('.filter-modal-content');
      if (!modalElement) {
        e.preventDefault();
      }
    };

    // Add overflow hidden to body to prevent background scrolling
    document.body.style.overflow = 'hidden';

    window.addEventListener('wheel', preventBodyScroll, { passive: false });
    window.addEventListener('touchmove', preventBodyScroll, { passive: false });

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('wheel', preventBodyScroll);
      window.removeEventListener('touchmove', preventBodyScroll);
    };
  }, [isOpen]);

  const handleTagToggle = (tag) => {
    setLocalSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleClearAll = () => {
    setLocalSearchQuery('');
    setLocalSelectedTags([]);
  };

  const handleApply = () => {
    // Start the close animation first
    setVisible(false);
    // Apply filters after animation completes
    setTimeout(() => {
      onSearchChange(localSearchQuery);
      onTagsChange(localSelectedTags);
      onApplyFilters();
      onClose && onClose();
    }, 300);
  };

  const handleCancel = () => {
    setLocalSearchQuery('');
    setLocalSelectedTags([]);
    handleClose();
  };

  const handleClose = () => {
    setVisible(false);
    // Wait for animation to complete
    setTimeout(() => onClose && onClose(), 300);
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-300 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={handleClose}
    >
      <div className="absolute inset-0 bg-black/70" />
      <div
        className={`filter-modal-content bg-gray-800 rounded-xl w-full max-w-lg mx-4 max-h-[80vh] overflow-hidden shadow-2xl transform transition-all duration-300 ${
          visible ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-6 scale-95 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-700">
          <h3 className="text-white text-xl font-semibold">Filter Projects</h3>
          <button 
            onClick={handleCancel} 
            className="text-gray-400 hover:text-white text-2xl transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-140px)]">
          {/* Search Box */}
          <div className="mb-6">
            <label className="block text-white text-sm font-medium mb-2">
              Search Projects
            </label>
            <input
              type="text"
              placeholder="Search by title, description, or technology..."
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleApply()}
              className="w-full px-4 py-3 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
              autoFocus
            />
          </div>

          {/* Tags/Skills Chooser */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <label className="block text-white text-sm font-medium">
                Filter by Skills/Technologies
              </label>
              {localSelectedTags.length > 0 && (
                <button
                  onClick={() => setLocalSelectedTags([])}
                  className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
                >
                  Clear Tags
                </button>
              )}
            </div>
            
            {localSelectedTags.length > 0 && (
              <div className="mb-3">
                <div className="text-xs text-gray-400 mb-2">Selected ({localSelectedTags.length}):</div>
                <div className="flex flex-wrap gap-2">
                  {localSelectedTags.map(tag => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-3 py-1 bg-blue-400 text-black text-sm rounded-lg"
                    >
                      {tag}
                      <button
                        onClick={() => handleTagToggle(tag)}
                        className="ml-2 text-black hover:text-gray-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="max-h-48 overflow-y-auto border border-gray-600 rounded-lg modal-tags-scroll">
              {skills.map(skill => (
                <button
                  key={skill}
                  onClick={() => handleTagToggle(skill)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-700 last:border-b-0 transition-colors ${
                    localSelectedTags.includes(skill)
                      ? 'bg-blue-400 text-black'
                      : 'text-gray-200 hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{skill}</span>
                    {localSelectedTags.includes(skill) && (
                      <span className="text-black">✓</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-700 bg-gray-750">
          <button
            onClick={handleCancel}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="px-6 py-2 bg-blue-400 text-black rounded-lg hover:bg-blue-300 transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterModal;
